import { useEffect, useState } from 'react';

import { ApolloQueryResult, FetchResult, useMutation, useQuery } from '@apollo/client';

import { CREATE_REVIEW, GET_LEARNER_REVIEW } from '~/graphql/videos';
import useAuth from '~/hooks/useAuth';
import {
  Mutation,
  Query,
  QueryGetLearnerReviewOnEntityArgs,
  ReviewableType,
  ReviewCreateInput,
  ReviewStatus,
} from '~/types';

export interface IUseVideoReview {
  setLikedOutsideCommentSection: (value: ((prevState: boolean) => boolean) | boolean) => void;
  thumbSelected: boolean | undefined;
  leaveReview: (options: {
    liked: boolean;
    comment?: string;
  }) => Promise<FetchResult<Pick<Mutation, 'createReview'>>>;
  likedVideo: undefined | boolean;
  likedOutsideCommentSection: boolean;
  refetchLearnerReview: (
    variables?: Partial<QueryGetLearnerReviewOnEntityArgs>,
  ) => Promise<ApolloQueryResult<Pick<Query, 'getLearnerReviewOnEntity'>>>;
  status: ReviewStatus | null | undefined;
  loadingReviewSubmit: boolean;
}

const useVideoReview = ({ videoId, isXApiPage }): IUseVideoReview => {
  const [likedVideo, setLikedVideo] = useState<boolean | undefined>(undefined);
  const [likedOutsideCommentSection, setLikedOutsideCommentSection] = useState(false);

  const { isAuthenticated } = useAuth();

  const [createReview, { loading: loadingReviewSubmit }] = useMutation<
    Pick<Mutation, 'createReview'>,
    { input: ReviewCreateInput }
  >(CREATE_REVIEW);

  const { data: learnerReviewData, refetch: refetchLearnerReview } = useQuery<
    Pick<Query, 'getLearnerReviewOnEntity'>,
    QueryGetLearnerReviewOnEntityArgs
  >(GET_LEARNER_REVIEW, {
    variables: {
      entity_type: ReviewableType.Video,
      entity_id: Number(videoId),
    },
    skip: !videoId || !isAuthenticated || isXApiPage,
    fetchPolicy: 'network-only',
  });

  useEffect(() => {
    setLikedVideo(undefined);
    setLikedOutsideCommentSection(false);
  }, [videoId]);

  const leaveReview = (options: { liked: boolean; comment?: string }) => {
    const { liked, comment } = options;

    setLikedVideo(liked);
    return createReview({
      variables: {
        input: {
          ...(comment && { comment }),
          stars_rating: liked ? 5 : 1,
          reviewable_type: ReviewableType.Video,
          reviewable_id: Number(videoId),
        },
      },
    });
  };

  return {
    likedVideo,
    likedOutsideCommentSection,
    setLikedOutsideCommentSection,
    leaveReview,
    status: learnerReviewData?.getLearnerReviewOnEntity?.status,
    thumbSelected:
      learnerReviewData?.getLearnerReviewOnEntity?.stars_rating &&
      learnerReviewData?.getLearnerReviewOnEntity?.status !== ReviewStatus.Rejected
        ? learnerReviewData?.getLearnerReviewOnEntity?.stars_rating > 3
        : undefined,
    refetchLearnerReview,
    loadingReviewSubmit,
  };
};

export default useVideoReview;
